import { useTypedSelector } from '@/store/store';
import { selectSinglePost } from '@/store/reducers/single-post';
import { selectSeoTemplate } from '@/store/reducers/seo-template';
import useSettingItem from '@/hooks/useSettingItem';
import { getFirstDateOfYear, getLastDateOfYear } from '@/utils/date';
import { getEventMicromarking } from '@/utils/seo';
import { usePageUrl } from '@/hooks/usePageUrl';
import { selectPostsListSeo } from '@/store/reducers/posts-list';
import { BlogCategory } from '@/typings/model';

import { getSinglePostSEO } from './helpers';

export const usePostsSeo = (category: BlogCategory) => {
  const seo = useTypedSelector(selectPostsListSeo);
  const organization = useSettingItem('ORGANIZATION_NAME');
  const address = useSettingItem('MAIN_ADDRESS');
  const url = usePageUrl();

  const seoParams = {
    title: seo?.pageTitle ?? '',
    description: seo?.pageDescription ?? '',
    openGraphImage: seo?.openGraphImage ?? '',
  };

  const micromarkingNames = {
    news: ['✔️ Последние новости', '🔥 Новинки авто', '🔥 Свежие новости'],
    offers: [
      '✔️ Приятные цены',
      '🔥 Выгодные предложения',
      '🔥 Высокое качество',
    ],
    reviews: [
      '✔️ Последние обзоры',
      '🔥 Новые авто',
      `🔥 Обзоры на автомобили ${new Date().getFullYear()}`,
    ],
    brand: [
      '✔️ Бренд',
      '🔥 Новые авто',
      `🔥 Обзор на бренд ${new Date().getFullYear()}`,
    ],
  };

  const micromarkingData = micromarkingNames[category].map((name) => ({
    image: seoParams.openGraphImage,
    name,
    description: seoParams.description,
    startDate: getFirstDateOfYear(),
    endDate: getLastDateOfYear(),
    url,
    organization: organization ?? '',
    address: address ?? '',
  }));

  return {
    ...seoParams,
    eventMicromarking: micromarkingData.map((data) =>
      getEventMicromarking(data)
    ),
  };
};

export const useSinglePostSeo = (category: BlogCategory) => {
  const page = useTypedSelector(selectSinglePost);
  const seoTemplate = useTypedSelector(selectSeoTemplate);
  const convertedSeoTemplate = getSinglePostSEO(
    page?.title ?? '',
    page?.image?.url ?? '',
    seoTemplate
  );
  const organization = useSettingItem('ORGANIZATION_NAME');
  const address = useSettingItem('MAIN_ADDRESS');
  const url = usePageUrl();

  const seoParams = {
    h1: convertedSeoTemplate?.h1 ?? '',
    title: page?.pageTitle ?? convertedSeoTemplate?.title ?? '',
    description:
      page?.pageDescription ?? convertedSeoTemplate?.description ?? '',
    openGraphImage:
      page?.openGraphImage ?? convertedSeoTemplate?.openGraphImage ?? '',
    keywords: convertedSeoTemplate?.keywords ?? '',
  };

  const micromarkingNames = {
    news: ['✔️ Последние новости', '🔥 Новинки авто', '🔥 Свежие новости'],
    offers: [
      '✔️ Приятные цены',
      '🔥 Выгодные предложения',
      '🔥 Высокое качество',
    ],
    reviews: [
      '✔️ Последние обзоры',
      '🔥 Новые авто',
      `🔥 Обзоры на автомобили ${new Date().getFullYear()}`,
    ],
    brand: [
      '✔️ Бренд',
      '🔥 Новые авто',
      `🔥 Обзор на бренд ${new Date().getFullYear()}`,
    ],
  };

  const micromarkingData = micromarkingNames[category].map((name) => ({
    image: seoParams.openGraphImage,
    name,
    description: seoParams.description,
    startDate: page?.datePublished ?? getFirstDateOfYear(),
    endDate: getLastDateOfYear(),
    url,
    organization: organization ?? '',
    address: address ?? '',
  }));

  return {
    ...seoParams,
    eventMicromarking: micromarkingData.map((data) =>
      getEventMicromarking(data)
    ),
  };
};
